import React, { useContext, useEffect, useState } from "react";
import { Typography, Box, TextField, Button, Select, MenuItem, FormControl } from "@mui/material";
import { useSnackbar } from "../shared/SnackbarProvider";
import { Repo } from "../Repo";

const images = [
  "/images/stickers/bee-regular.png",
  "/images/stickers/bee-blush.png",
  "/images/stickers/bee-hair.png",
  "/images/stickers/bee-sick.png",
  "/images/stickers/bee-smart.png",
  "/images/stickers/bee-antennae.png",
  "/images/stickers/bee-pirate.png",
  "/images/stickers/bee-chef.png",
  "/images/stickers/bee-space.png",
  "/images/stickers/bee-painter.png",
  "/images/stickers/bee-hero.png",
  "/images/stickers/bee-cowboy.png",
  "/images/stickers/bee-scary.png",
  "/images/stickers/bee-merry.png",
  "/images/stickers/bee-easter.png",
  "/images/stickers/bee-love.png",
  "/images/stickers/bee-study.png",
  "/images/stickers/bee-swim.png",
  "/images/stickers/bee-magic.png",
  // "/images/stickers/bee-calibration.png"
];

const STICKER_SIZE = 225;
const PRINT_WIDTH = 793;
const PRINT_HEIGHT = 1123;

const center = STICKER_SIZE / 2;
const radius = STICKER_SIZE * 0.4
const startX = STICKER_SIZE * 0.1;
const endX = STICKER_SIZE - startX;

const StickerCell: React.FC<{ sticker: { topText: string; bottomText: string; imageIndex: number }, index: number, onSwap: (index: number) => void }> = ({ sticker, index, onSwap }) => {
  return (
    <svg
      width={STICKER_SIZE}
      height={STICKER_SIZE}
      viewBox={`0 0 ${STICKER_SIZE} ${STICKER_SIZE}`}
      style={{ cursor: 'pointer' }}
      onClick={() => onSwap(index)}
    >
      <defs>
        <filter id={`shadow-${index}`} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="3" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.5" />
        </filter>
        <path id={`topPath-${index}`} d={`M ${startX},${center} A ${radius},${radius} 0 0,1 ${endX},${center}`} />
        <path id={`bottomPath-${index}`} d={`M ${startX},${center} A ${radius},${radius} 0 0,0 ${endX},${center}`} />
      </defs>
      <image
        href={images[sticker.imageIndex]}
        x="0"
        y="0"
        width={STICKER_SIZE}
        height={STICKER_SIZE}
      />
      <text fill="#a9388f" fontSize="23" fontWeight="bold" filter={`url(#shadow-${index})`}>
        <textPath href={`#topPath-${index}`} startOffset="50%" textAnchor="middle">
          {sticker.topText}
        </textPath>
      </text>
      <text fill="#a9388f" fontSize="23" fontWeight="bold" filter={`url(#shadow-${index})`}>
        <textPath href={`#bottomPath-${index}`} startOffset="50%" textAnchor="middle">
          {sticker.bottomText}
        </textPath>
      </text>
    </svg>
  );
};

const StickersPage: React.FC = () => {
  const snack = useSnackbar();
  const repo = new Repo();
  const [stickers, setStickers] = useState(Array.from({ length: 12 }, () => ({ topText: "", bottomText: "", imageIndex: 0 })));
  const [formTopText, setFormTopText] = useState("Bee Happy Preschool");
  const [formBottomText, setFormBottomText] = useState("Hedgerley SL2 3UU");

  const handleSwap = (index: number) => {
    setStickers(prev => prev.map((sticker, i) => i === index ? { ...sticker, imageIndex: (sticker.imageIndex + 1) % images.length } : sticker));
  };

  const handleSync = () => {
    const imgIndex = stickers[0].imageIndex;
    setStickers(prev => prev.map(s => ({ ...s, imageIndex: imgIndex })));
  };

  const applyTextToRow = (rowNum: number) => {
    setStickers(prev => prev.map((sticker, i) => {
      if (i >= rowNum * 3 && i < rowNum * 3 + 3) {
        return { ...sticker, topText: formTopText, bottomText: formBottomText };
      }
      return sticker;
    }));
  };

  const applyTextToAll = () => {
    setStickers(prev => prev.map(sticker => ({ ...sticker, topText: formTopText, bottomText: formBottomText })));
  };

  useEffect(() => {
    // Prefetch sticker images to avoid delay on first swap/change
    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);


  return (
    <Box>
      <Box className="no-print" sx={{ marginBottom: 2 }}>
        <Typography variant="h5">Sticker Controls</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginTop: 1, flexWrap: 'wrap' }}>
          <TextField label="Top Text" variant="outlined" value={formTopText} onChange={(e) => setFormTopText(e.target.value)} />
          <TextField label="Bottom Text" variant="outlined" value={formBottomText} onChange={(e) => setFormBottomText(e.target.value)} />
          <Button variant="contained" onClick={() => applyTextToRow(0)}>Row 1</Button>
          <Button variant="contained" onClick={() => applyTextToRow(1)}>Row 2</Button>
          <Button variant="contained" onClick={() => applyTextToRow(2)}>Row 3</Button>
          <Button variant="contained" onClick={() => applyTextToRow(3)}>Row 4</Button>
          <Button variant="contained" onClick={applyTextToAll}>All Cells</Button>
          <Button variant="contained" onClick={handleSync}>Sync Images</Button>
          <Button variant="contained" onClick={() => window.print()}>Print</Button>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={stickers[0]?.imageIndex ?? 0}
              onChange={(e) => {
                const idx = Number((e.target as HTMLInputElement).value);
                setStickers(prev => prev.map((s, i) => i === 0 ? { ...s, imageIndex: idx } : s));
              }}
            >
              {images.map((img, idx) => (
                <MenuItem key={img} value={idx}>{img.split('/').pop()}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box className="printable" sx={{ width: `${PRINT_WIDTH}px`, height: `${PRINT_HEIGHT}px`, margin: '0 auto', padding: 0, boxSizing: 'border-box' }}>
        <div className="element top-offset"></div>
        {[0, 1, 2, 3].map(row => (
          <>
            <div key={row} style={{ width: `${PRINT_WIDTH}px`, height: `${STICKER_SIZE}px` }} className="element">
              <div className="element side"></div>

              <div style={{ width: `${STICKER_SIZE}px`, height: `${STICKER_SIZE}px` }} className="element">
                <StickerCell sticker={stickers[row * 3]} index={row * 3} onSwap={handleSwap} />
              </div>

              <div className="element cell-gap"></div>

              <div style={{ width: `${STICKER_SIZE}px`, height: `${STICKER_SIZE}px` }} className="element">
                <StickerCell sticker={stickers[row * 3 + 1]} index={row * 3 + 1} onSwap={handleSwap} />
              </div>

              <div className="element cell-gap"></div>

              <div style={{ width: `${STICKER_SIZE}px`, height: `${STICKER_SIZE}px` }} className="element">
                <StickerCell sticker={stickers[row * 3 + 2]} index={row * 3 + 2} onSwap={handleSwap} />
              </div>

              <div className="element side"></div>
            </div>
            <div key={row} className="element row-gap">
            </div>
          </>
        ))}
      </Box>

      <style>{`
        .printable {
          position: relative;
          border: 1px solid #00a;
          box-sizing: border-box;
        }
        .element {
          border: 1px solid #a00;
          border-color: #fff;
          box-sizing: border-box;
          display: inline-block;
          vertical-align: top;
          margin: 0;
          padding: 0;
        }
        .side {
          width: 23px;
          height: 20px;
        }
        .cell-gap {
          width: 32px;
          height: 5px;
        }
        .row-gap {
          height: 28px;
        }
        .top-offset {
          height: 65px;
        }

      `}</style>
    </Box>
  );
};

export default StickersPage;
